import {
  LOAD_LIBRARY,
  LoadLibraryAction, loadLibrarySuccess,
  CREATE_APPLICATION,
  CreateApplicationAction,
  createApplicationSuccess,
  UnsubscribeFromEmailsAction,
  UNSUSCRIBE_FROM_EMAILS,
  unsubscribeFromEmailsSuccess,
  SubmitPreQualificationAction,
  submitPreQualificationSuccess,
  SUBMIT_PRE_QUALIFICATION,
  setPreQualificationFlag,
  SubmitDealerApplicationAction,
  submitDealerApplicationSuccess,
  SUBMIT_DEALER_APPLICATION,
  LoadStatesAction,
  loadStatesSuccess,
  LOAD_STATES,
  LoadCustomerReferenceRelationshipsAction,
  loadCustomerReferenceRelationshipsSuccess,
  LOAD_CUSTOMER_REFERENCE_RELATIONSHIPS,
  UpdateApplicationAction,
  UPDATE_APPLICATION,
  CreateDownPaymentLinkAction,
  CREATE_DOWN_PAYMENT_LINK,
  createDownPaymentLinkSuccess,
  SetIsLoadingDownPaymentLink
} from './actions';
import { put, takeLatest, all, call } from 'redux-saga/effects';
import { httpGet, httpPost, httpPut } from '../../Http/HttpClient';
import { LookupItems } from '../../Models/LookupItems';
import { displayError, isValidURL } from '../../Helpers/Utilities';
import { isNil } from 'ramda';
import { PreQualifyResult } from '../../Models/PreQualifyResult';
import { State } from '../../Models/State';
import { CustomerReferenceRelationship } from '../../Models/CustomerReferenceRelationship';
import {CreateApplicationFromFormResults} from "../../Models/CreateApplicationFromFormResults";
import {CuratedSiteDownPaymentRequestDetails} from "../../Models/CuratedSiteDownPaymentRequestDetails";

function* loadLibrarySaga(action: LoadLibraryAction) {
  try {
    const apiUrl = isNil(action.payload.locationId)
      ? `Lookup/creditApplication?clientId=${action.payload.clientId.toUpperCase()}`
      : `Lookup/creditApplication?clientId=${action.payload.clientId.toUpperCase()}&locationId=${action.payload.locationId!.toUpperCase()}`;
    const response: { ok: boolean, json: any, message?: any } = yield call(httpGet, apiUrl,);

    if (response && response.ok) {
      const lookupItems: LookupItems = yield call([response, response.json]);
      yield put(loadLibrarySuccess(lookupItems));
    } else {
      if (!response) {
        yield put(displayError('It appears that you cannot connect to the api. Make sure you have an active internet connection.'));
      } else {
        yield put(displayError('An unexpected error has occurred when loading library'));
      }
    }
  } catch (error: any) {
    yield put(displayError(error?.message ?? error as string));
  }
}

export function* watchLibrary() {
  yield takeLatest(LOAD_LIBRARY, loadLibrarySaga);
}

// Load States
function* loadStatesSaga(action: LoadStatesAction) {
  try {
    const response: { ok: boolean, json: any, message?: any } = yield call(httpGet, `Lookup/states?customerPortalLinkId=${action.payload.customerPortalLinkId}`);
    if (response && response.ok) {
      const states: State[] = yield call([response, response.json]);
      yield put(loadStatesSuccess(states));
    } else {
      if (!response) {
        yield put(displayError('It appears that you cannot connect to the api. Make sure you have an active internet connection.'));
      } else {
        yield put(displayError('An error occured when getting list of states.'));
      }
    }
  } catch (error: any) {
    yield put(displayError(error?.message ?? error as string));
  }
}

export function* watchStates() {
  yield takeLatest(LOAD_STATES, loadStatesSaga);
}

function* createApplicationSaga(action: CreateApplicationAction) {
  try {
    const apiUrl = isNil(action.payload.locationId)
      ? `Application/createFromOnlineForm?clientId=${action.payload.clientId.toUpperCase()}&useCuratedSite=${action.payload.useCuratedSite}`
      : `Application/createFromOnlineForm/${action.payload.locationId}?clientId=${action.payload.clientId.toUpperCase()}&useCuratedSite=${action.payload.useCuratedSite}`;

    const response: { json: any, ok: boolean } = yield call(httpPost,
      apiUrl,
      action.payload.application
    );

    // API will return either a redirect url or an error message.
    const result: { redirectUrl?: string } | { error?: string } = yield call([response, response.json]);
    if ((result as { error: string })?.error) {
      yield put(displayError(result as string));
    } else {
      // We will redirect to the custom url if one exists. Empty string indicates we need to redirect to the portal login page.
      yield put(createApplicationSuccess(result as CreateApplicationFromFormResults));
    }
  } catch (error) {
    yield put(displayError(error as string));
  }
}

export function* watchCreateApplication() {
  yield takeLatest(CREATE_APPLICATION, createApplicationSaga);
}

// Update application
function* updateApplicationSaga(action: UpdateApplicationAction) {
  try {
    const apiUrl = action.payload.customerId
      ? `application/updateApplication/${action.payload.customerId}?clientId=${action.payload.clientId.toUpperCase()}&customerPortalLinkId=${action.payload.customerPortalLinkId}`
      : `application/updateApplication?clientId=${action.payload.clientId.toUpperCase()}&customerPortalLinkId=${action.payload.customerPortalLinkId}`;
    const response: { json: any, ok: boolean } = yield call(httpPut,
      apiUrl,
      action.payload.application
    );
    // API will return either a redirect url or an error message.
    const result: CreateApplicationFromFormResults | { error?: string } = yield call([response, response.json]);
    if (!response.ok || (result as { error: string })?.error) {
      yield put(displayError(response.json as string));
    } else {
      // We will redirect to the custom url if one exists. Empty string indicates we need to redirect to the portal login page.
      yield put(createApplicationSuccess(result as CreateApplicationFromFormResults));
    }
  } catch (error) {
    yield put(displayError(error as string));
  }
}

export function* watchUpdateApplication() {
  yield takeLatest(UPDATE_APPLICATION, updateApplicationSaga);
}


function* unsubscribeFromEmailsSaga(action: UnsubscribeFromEmailsAction) {
  try {

    const response: { json: any } = yield call(httpPost,
      `Email/unsubscribe/${action.payload.emailAddress}/?clientId=${action.payload.clientId.toUpperCase()}`,
      action.payload,
    );
    const result: { message: string } = yield call([response, response.json]);
    if (result?.message) {
      yield put(unsubscribeFromEmailsSuccess(result.message));
    } else {
      yield put(displayError('Unexpected error occured while unsubscribing.'));
    }
  } catch (error) {
    yield put(displayError(error as string));
  }
}

export function* watchUnsubscribeFromEmails() {
  yield takeLatest(UNSUSCRIBE_FROM_EMAILS, unsubscribeFromEmailsSaga);
}

function* submitPreQualifySaga(action: SubmitPreQualificationAction) {
  try {
    const apiUrl = isNil(action.payload.locationId)
      ? `Credit/preQualify?clientId=${action.payload.preQualifySubmissionInfo.clientId.toUpperCase()}`
      : `Credit/preQualify/${action.payload.locationId}?clientId=${action.payload.preQualifySubmissionInfo.clientId.toUpperCase()}`;
    const response: { json: any } = yield call(httpPost, apiUrl, action.payload.preQualifySubmissionInfo);
    const preQualifyResponse: { result: PreQualifyResult } = yield call([response, response.json]);
    if (preQualifyResponse && preQualifyResponse.result) {
      yield put(setPreQualificationFlag(false));
      yield put(submitPreQualificationSuccess(preQualifyResponse.result));
    } else {
      yield put(setPreQualificationFlag(false));
      yield put(displayError("Unable to complete pre-qualification request."));
    }
  } catch (error) {
    yield put(displayError(error as string));
  }
}

export function* watchSubmitPreQualify() {
  yield takeLatest(SUBMIT_PRE_QUALIFICATION, submitPreQualifySaga);
}
/** End Client Lookups */

/** Dealer Application Form */
function* submitDealerApplicationFormSaga(action: SubmitDealerApplicationAction) {
  try {
    const response: { json: any } = yield call(httpPost, `Application/createFromDealerApp?clientId=${action.payload.clientId.toUpperCase()}`, action.payload.application);
    const result: Response | { error?: string } = yield call([response, response.json]);
    if ((result as { error: string })?.error) {
      yield put(displayError(result as string));
    } else {
      yield put(submitDealerApplicationSuccess());
    }
  } catch (error) {
    yield put(displayError(error as string));
  }
}

export function* watchSubmitDealerApplicationForm() {
  yield takeLatest(SUBMIT_DEALER_APPLICATION, submitDealerApplicationFormSaga);
}
/** End Dealer Application Form */

function* loadCustomerReferenceRelationshipsSaga(action: LoadCustomerReferenceRelationshipsAction) {
  try {
    const response: { ok: boolean, json: any, message?: any } = yield call(httpGet, `Lookup/customerReferenceRelationships?customerPortalLinkId=${action.payload.customerPortalLinkId}`);

    if (response && response.ok) {
      const customerReferenceRelationships: CustomerReferenceRelationship[] = yield call([response, response.json]);
      yield put(loadCustomerReferenceRelationshipsSuccess(customerReferenceRelationships));
    } else {
      if (!response) {
        yield put(displayError('It appears that you cannot connect to the api. Make sure you have an active internet connection.'));
      } else {
        yield put(displayError('An unexpected error has occurred when loading customer reference relationships.'));
      }
    }
  } catch (error) {
    yield put(displayError(error as string));
  }
}

export function* watchLoadCustomerReferenceRelationships() {
  yield takeLatest(LOAD_CUSTOMER_REFERENCE_RELATIONSHIPS, loadCustomerReferenceRelationshipsSaga);
}

// Create Down Payment Link
function* createDownPaymentLinkSaga(action: CreateDownPaymentLinkAction) {
  try {
    const response: { ok: boolean, json: any, message?: any  } = yield call(httpPost, `DownPayment/createLink?clientId=${action.payload.clientId.toUpperCase()}`, action.payload);
    if (!response.ok) {
      yield put(displayError(response.message as string));
      yield put(SetIsLoadingDownPaymentLink(false));
    } else {
      // Ensure the link returns is a valid link. If not, display an error.
      const downPaymentLinkResult: CuratedSiteDownPaymentRequestDetails = yield call([response, response.json]);
      if (isValidURL(downPaymentLinkResult.link)) {
        yield put(createDownPaymentLinkSuccess(downPaymentLinkResult));
      } else {
        yield put(displayError(downPaymentLinkResult.link));
        yield put(SetIsLoadingDownPaymentLink(false));
      }
    }
  } catch (error) {
    yield put(displayError(error as string));
    yield put(SetIsLoadingDownPaymentLink(false));
  }
}

export function* watchCreateDownPaymentLink() {
  yield takeLatest(CREATE_DOWN_PAYMENT_LINK, createDownPaymentLinkSaga);
}

export default function* sharedSagas() {
  yield all([
    watchLibrary(),
    watchCreateApplication(),
    watchUnsubscribeFromEmails(),
    watchSubmitPreQualify(),
    watchSubmitDealerApplicationForm(),
    watchStates(),
    watchLoadCustomerReferenceRelationships(),
    watchUpdateApplication(),
    watchCreateDownPaymentLink()
  ]);
}
